import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { FaMoneyBillWave } from "react-icons/fa";
import ExportOptions from "../../ExportOptions";
import TableSkeleton from "../../TableSkeleton";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const LEDGER_URL = "https://api.muktihospital.com/api/nicu/refer-ledger/refer-ledger";
const PAY_URL = "https://api.muktihospital.com/api/nicu/refer-ledger/pay-commission";

const filterOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "daily", label: "Daily" },
  { value: "yearly", label: "Yearly" },
  { value: "all", label: "All Time" },
];

// ---- Pay Commission Modal ----
const PayModal = ({ isOpen, onClose, bill, onPaid }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && bill) {
      setAmount(bill.commissionDue > 0 ? String(bill.commissionDue) : "");
      setNote("");
      setLoading(false);
    }
  }, [isOpen, bill]);

  if (!isOpen || !bill) return null;

  const handlePay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (amt > bill.commissionDue) { toast.error(`Max due is ৳${bill.commissionDue}.`); return; }
    try {
      setLoading(true);
      await axios.post(PAY_URL, { billNumber: bill.billNumber, agentName: bill.refer, amount: amt, note }, { headers: { "x-api-key": API_KEY } });
      toast.success("Commission paid!");
      onPaid();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to pay.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-bold text-gray-700">Pay Commission</h2>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Agent:</span><span className="font-medium">{bill.refer || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Patient:</span><span className="font-medium">{bill.patientName || "N/A"}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Bill No:</span><span className="font-medium">{bill.billNumber}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Commission:</span><span className="font-medium">৳{bill.referAmount}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Already Paid:</span><span className="font-medium text-green-600">৳{bill.commissionPaid}</span></div>
          <div className="flex justify-between border-t pt-2"><span className="text-gray-500">Due:</span><span className="font-bold text-red-600">৳{bill.commissionDue}</span></div>
          <div>
            <label className="block text-gray-600 mb-1">Pay Amount (৳)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full p-2 border rounded-md text-sm" placeholder="0" min="0" disabled={loading} />
          </div>
          <div>
            <label className="block text-gray-600 mb-1">Note</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className="w-full p-2 border rounded-md text-sm" placeholder="Optional" disabled={loading} />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button onClick={onClose} className="bg-[#E7633D]/90 text-white text-sm py-2 px-5 rounded-md hover:bg-[#E7633D]" disabled={loading}>Cancel</button>
          <button onClick={handlePay} className="bg-M-primary-color/90 text-white text-sm py-2 px-5 rounded-md hover:bg-M-primary-color" disabled={loading || bill.commissionDue <= 0}>
            {loading ? "Paying..." : "Pay Commission"}
          </button>
        </div>
      </div>
    </div>
  );
};

const NicuReferLedger = () => {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({ totalCommission: 0, totalPaid: 0, totalDue: 0, totalBills: 0 });
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [payBill, setPayBill] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const itemsPerPage = 10;

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterType === "daily") { params.startDate = startDate; params.endDate = endDate; }
    else if (filterType === "monthly") { const [y, m] = selectedMonth.split("-"); params.year = y; params.month = m; }
    else if (filterType === "yearly") { params.year = selectedYear; }

    const cacheKey = `nicu_refer_ledger_${filterType}_${JSON.stringify(params)}`;

    try {
      const res = await axios.get(LEDGER_URL, { headers: { "x-api-key": API_KEY }, params });
      setBills(res.data.bills || []);
      setSummary(res.data.summary || { totalCommission: 0, totalPaid: 0, totalDue: 0, totalBills: 0 });

      // Cache this filter for offline viewing.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        bills: res.data.bills || [],
        summary: res.data.summary || { totalCommission: 0, totalPaid: 0, totalDue: 0, totalBills: 0 },
      });
    } catch (err) {
      console.error("Error loading ledger:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.bills) {
          setBills(cached.bills);
          setSummary(cached.summary || { totalCommission: 0, totalPaid: 0, totalDue: 0, totalBills: 0 });
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved refer ledger.");
        } else {
          setBills([]);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load refer ledger.");
        setBills([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, refreshTrigger]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return bills.filter((b) =>
      (b.refer || "").toLowerCase().includes(q) ||
      (b.patientName || "").toLowerCase().includes(q) ||
      (b.billNumber || "").toLowerCase().includes(q)
    );
  }, [bills, searchQuery]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const statusBadge = (s) => {
    const map = { Paid: "bg-green-100 text-green-700", Partial: "bg-amber-100 text-amber-700", Unpaid: "bg-red-100 text-red-700" };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
  };

  const headers = ["Agent", "Phone", "Patient", "Bill No", "Commission", "Paid", "Due", "Status"];
  const dataMapper = (b) => [b.refer, b.agentPhone, b.patientName, b.billNumber, b.referAmount, b.commissionPaid, b.commissionDue, b.commissionStatus].join("\t");
  const breadcrumbs = useMemo(() => [{ label: "Dashboard", url: "/" }, { label: "NICU" }, { label: "Refer Ledger" }], []);

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden"><PageHeading title="NICU Refer Ledger" breadcrumbs={breadcrumbs} /></div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 print-hidden">
        <div className="bg-white p-4 rounded-lg shadow-sm"><p className="text-xs text-gray-500">Total Commission</p><p className="text-xl font-black text-blue-600">৳{Math.round(summary.totalCommission)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow-sm"><p className="text-xs text-gray-500">Total Paid</p><p className="text-xl font-black text-green-600">৳{Math.round(summary.totalPaid)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow-sm"><p className="text-xs text-gray-500">Total Due</p><p className="text-xl font-black text-red-600">৳{Math.round(summary.totalDue)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow-sm"><p className="text-xs text-gray-500">Total Bills</p><p className="text-xl font-black text-gray-700">{summary.totalBills}</p></div>
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <h2 className="text-base font-medium text-gray-700">Commission List</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-32">
              <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} placeholder="Filter" />
            </div>
            {filterType === "daily" && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
              </>
            )}
            {filterType === "monthly" && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm" />}
            {filterType === "yearly" && <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm w-24" min="2000" max="2100" />}
            <input type="text" placeholder="Search agent/patient..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-48 w-full p-2 border rounded-md text-sm" />
            <ExportOptions data={currentData} dropdownOpen={dropdownOpen} toggleDropdown={() => setDropdownOpen(!dropdownOpen)} title="NICU Refer Ledger" headers={headers} dataMapper={dataMapper} />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <table className="min-w-[900px] w-full"><TableSkeleton rowCount={itemsPerPage} columns={[{ width: "120px" }, { width: "100px" }, { width: "120px" }, { width: "90px" }, { width: "80px" }, { width: "70px" }, { width: "70px" }, { width: "70px" }, { width: "70px" }]} /></table>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No commission records found.</div>
          ) : (
            <table className="min-w-[900px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Agent</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Phone</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Patient</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Bill No</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Commission</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Paid</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Due</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Bill Status</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Commission</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700 print-hidden-in-print">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((b) => (
                  <tr key={b.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-700 capitalize">{b.refer || "N/A"}</td>
                    <td className="p-3 text-sm text-gray-500">{b.agentPhone}</td>
                    <td className="p-3 text-sm text-gray-700">{b.patientName || "N/A"}</td>
                    <td className="p-3 text-sm text-gray-500">{b.billNumber}</td>
                    <td className="p-3 text-sm text-right font-medium">৳{b.referAmount}</td>
                    <td className="p-3 text-sm text-right text-green-600">৳{b.commissionPaid}</td>
                    <td className="p-3 text-sm text-right text-red-600 font-bold">৳{b.commissionDue}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.isCleared ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {b.billStatus}
                      </span>
                    </td>
                    <td className="p-3 text-center">{statusBadge(b.commissionStatus)}</td>
                    <td className="p-3 text-center print-hidden-in-print">
                      <button
                        onClick={() => { setPayBill(b); setPayOpen(true); }}
                        disabled={b.commissionDue <= 0 || !b.isCleared}
                        title={!b.isCleared ? "Clear the patient's bill first" : ""}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${(b.commissionDue > 0 && b.isCleared) ? "bg-M-primary-color/10 text-M-primary-color hover:bg-M-primary-color hover:text-white" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                      >
                        <FaMoneyBillWave /> Pay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 flex justify-end gap-2 print-hidden">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowLeft /></button>
            <span className="p-2 text-sm">Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowRight /></button>
          </div>
        )}
      </div>

      <PayModal isOpen={payOpen} onClose={() => setPayOpen(false)} bill={payBill} onPaid={() => setRefreshTrigger((p) => p + 1)} />
    </div>
  );
};

export default NicuReferLedger;
